import {
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  inject,
  OnInit,
  DestroyRef,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SmartGridCellDirective } from './smart-grid-cell.directive';
import { SmartGridEditDirective } from './smart-grid-edit.directive';
import {
  GridColumn,
  GridFilter,
  GridGroup,
  GridSort,
  GroupNode,
  AggregateFilter,
  CellEditEvent,
  ColumnFilter,
} from './smart-grid.models';
import { ReportExportService } from '../report/report-export.service';
import { ReportColumn } from '../report/report.models';
// Use type-only imports for ExcelJS / file-saver so the runtime code is NOT
// bundled into the main chunk. The actual modules are loaded with dynamic
// `await import(...)` inside `exportToExcelFormatted()` — they only ship to
// the client when a user clicks Export. ExcelJS alone is ~700 kB of JS.
import type * as ExcelJSType from 'exceljs';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment'
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
type AggKind = 'sum' | 'count' | 'avg' | 'min' | 'max';
type AggCfg = { [field: string]: AggKind[] };
type AggMap = Record<string, Partial<Record<AggKind, number>>>;

@Component({
  selector: 'konti-smart-grid',
  standalone: true,
  imports: [CommonModule, FormsModule, SmartGridCellDirective, SmartGridEditDirective, NgbModule, BsDatepickerModule],
  templateUrl: './smart-grid.component.html',
  styleUrls: ['./smart-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmartGridComponent<T> implements AfterContentInit, AfterViewInit, OnChanges, OnInit {
  private filterChange$ = new Subject<GridFilter[]>();
  @Input() data: T[] = [];
  @Input() columns: GridColumn<T>[] = [];
  @Input() initialSort: GridSort[] = [];
  @Input() initialGroups: GridGroup[] = [];
  @Input() showGroupPanel = true;
  @Input() showFilterPanel = true;
  @Input() customMobile = false; // Set to true to use component's own mobile layout
  @Input() rowKey?: (row: T) => string | number;
  @Input() checkedColumn: string = '';
  // Emits when the header checkbox is toggled: includes the currently visible rows
  @Output() allChecked = new EventEmitter<{ checked: boolean; rows: T[] }>();
  // Emits when groups are changed (added or removed)
  @Output() groupChange = new EventEmitter<GridGroup[]>();

  /** Rad-klasser (brukes til approved/not-approved osv.) */
  @Input() rowClass?: (row: T) => string | string[] | Record<string, boolean>;

  /** Hvilke aggregeringer som skal beregnes pr. gruppe, f.eks: { quantity: ['sum'] } */
  @Input() groupAggregates: AggCfg = {};

  /** Filtre for aggregeringer - bestemmer hvilke rader som skal inkluderes i hver aggregering */
  @Input() aggregateFilters: AggregateFilter<T> = {};

  /** Vis totalsum nederst i gridet for alle aggregeringer */
  @Input() showTotalRow = false;

  /** Optional configuration for aggregation filter toggle */
  @Input() aggregateFilterConfig?: {
    enabled: boolean;
    label: string;
    filterFn: (row: T) => boolean;
    onToggle?: (enabled: boolean) => void;
  };

  /** Auto-collapse groups when total rows exceed this threshold (0 = disabled) */
  @Input() autoCollapseThreshold = 5000;

  /** Enable global search bar in the group panel */
  @Input() showGlobalSearch = false;

  /** Placeholder text for the global search input */
  @Input() globalSearchPlaceholder = 'Søk...';

  /** Enable Excel export functionality */
  @Input() enableExport = false;

  /** Export button label (default: 'Excel') */
  @Input() exportButtonLabel = 'Excel';

  /** Optional filename for export (without extension) */
  @Input() exportFilename?: string;

  @Output() rowClick = new EventEmitter<T>();
  @Output() cellEdit = new EventEmitter<CellEditEvent<T>>();

  // Inject ReportExportService
  private reportExportService = inject(ReportExportService);

  @ContentChildren(SmartGridCellDirective)
  cellTemplates!: QueryList<SmartGridCellDirective>;
  templateMap = new Map<string, SmartGridCellDirective>();

  @ContentChildren(SmartGridEditDirective)
  editTemplates!: QueryList<SmartGridEditDirective>;
  editTemplateMap = new Map<string, SmartGridEditDirective>();

  filters: GridFilter[] = [];
  sorts: GridSort[] = [];
  groups: GridGroup[] = [];

  /** Whether the inline filter row is visible */
  filterRowVisible = false;

  processedTree: GroupNode<T>[] = [];
  paginatedTree: GroupNode<T>[] = []; // Paginated subset of processedTree
  flatRows: T[] = [];

  // UI state for bulk expand/collapse (undefined => all levels)
  selectedGroupLevel: string = 'all';

  // Mobile card expand/collapse state
  mobileExpandedRows = new Set<string | number>();

  // Inline editing state
  editingCell: { rowKey: string | number, field: string } | null = null;
  editingValue: any = null;
  editError: string | null = null;

  /** Cache av aggregater per gruppenode */
  private nodeAgg = new WeakMap<object, AggMap>();
  aggFields: string[] = [];

  /** Total aggregater for hele datasett */
  totalAggregates: AggMap = {};

  textFilter = [];
  numberFilter = [];
  dateFilter = [];
  filterType: any = {};
  filterValue: any = {};
  @Input() loader = false;
  @Input() showFilterTypePanel = false;
  @Input() showResetButton = true;
  @Input() initialFilter: GridFilter[] = [];
  @Output() sortChange = new EventEmitter<GridSort[]>();
  @Input() noRecordsFoundPanel = false;
  @Input() noRecordsFoundText = "";
  @Input() bsConfig?: Partial<BsDatepickerConfig> = null;
  // Internal loading state for recompute operations
  isProcessing = false;

  /** 
   * Computed property: Show loader when external loader is true OR 
   * when grid is processing data internally (sorting, filtering, grouping)
   */
  get showLoader(): boolean {
    return this.loader || this.isProcessing;
  }
  @Output() filterChange = new EventEmitter<GridFilter[]>();
  @Input() showPagination = false;
  @Input() pageSizes: any[] = [];
  @Input() pageSize: number;
  // Default to page 1 — without this, paginated consumers that don't pass
  // [currentPage] end up with currentPage=undefined and loadData() slices
  // the row array with NaN bounds, producing an empty page.
  @Input() currentPage: number = 1;
  rows: T[] = [];
  @Input() totalRows: number = 0;
  @Output() pageChange = new EventEmitter();
  @Input() resetSetting: boolean = false;
  @Input() showFieldSum = false;
  @Input() serverSidePagination = false;
  /** Enable server-side filtering (disables client-side filter logic) */
  @Input() serverSideFilter = false;
  /** Debounce time for server-side filter events (ms) */
  @Input() serverSideFilterDebounce = 300;

  /** Enable server-side search - emits filter events instead of filtering client-side */
  @Input() serverSideSearch = false;
  /** Event emitted when column filters change (server-side mode) */
  @Output() serverFilterChange = new EventEmitter<ColumnFilter[]>();
  @Output() currentPageChange = new EventEmitter<number>();
  @Input() getAllCheckBox = false;
  checkedAllCheckBox: any = false;
  /** Active column filters for server-side search */
  private activeColumnFilters: ColumnFilter[] = [];
  /** Subject for debouncing server-side filter changes */
  private serverFilterSubject = new Subject<ColumnFilter[]>();

  /** Subject for debouncing client-side text/number filter input */
  private filterInputSubject = new Subject<{ field: string | number | symbol, type: GridColumn['filterType'], value: any, operator?: GridFilter['operator'] }>();

  /** Global search term */
  globalSearchTerm = '';
  /** Subject for debouncing global search */
  private globalSearch$ = new Subject<string>();
  private destroyRef = inject(DestroyRef);

  // ── Column resize ────────────────────────────────────────────────────────
  /**
   * Allow the user to drag the right edge of any header to resize the column.
   * The default is true; per-column opt-out via `GridColumn.resizable = false`.
   */
  @Input() resizableColumns = true;

  // Live state during a drag. We store the raw values rather than setting
  // `col.width` immediately so we can throttle to RAF if a future change
  // makes the column flicker. For now we update directly.
  private _resizeCol: GridColumn<T> | null = null;
  private _resizeStartX = 0;
  private _resizeStartWidth = 0;
  private _resizeMoveBound = (e: MouseEvent) => this.onResizeMove(e);
  private _resizeEndBound = () => this.onResizeEnd();

  // ── Auto-size on content ─────────────────────────────────────────────────
  /**
   * Measure rendered cell content and set column widths automatically after
   * the first render. OFF by default — many existing pages set widths
   * explicitly via `GridColumn.width`, and turning this on globally would
   * change those layouts. Pages that want it set `[autoSizeColumns]="true"`.
   */
  @Input() autoSizeColumns = false;

  /** Cap so a single very-wide cell does not blow out the grid. */
  @Input() maxAutoWidth = 500;

  /** Marks columns we have already auto-sized so we don't keep re-measuring. */
  private autoSizedFields = new Set<string>();

  constructor(
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private hostRef: ElementRef<HTMLElement>
  ) {
    this.textFilter = [
      { label: this.translateService.instant('FilterOperators.contains'), value: 'contains' },
      { label: this.translateService.instant('FilterOperators.startsWith'), value: 'startsWith' },
      { label: this.translateService.instant('FilterOperators.endsWith'), value: 'endsWith' },
      { label: this.translateService.instant('FilterOperators.equals'), value: 'eq' },
      { label: this.translateService.instant('FilterOperators.notEquals'), value: 'neq' }
    ];

    this.numberFilter = [
      { label: this.translateService.instant('FilterOperators.equals'), value: 'eq' },
      { label: '>', value: 'gt' },
      { label: '>=', value: 'gte' },
      { label: '<', value: 'lt' },
      { label: '<=', value: 'lte' },
      { label: this.translateService.instant('FilterOperators.notEquals'), value: 'neq' }
    ];

    this.dateFilter = [
      { label: this.translateService.instant('FilterOperators.equals'), value: 'eq' },
      { label: '>', value: 'gt' },
      { label: '>=', value: 'gte' },
      { label: '<', value: 'lt' },
      { label: '<=', value: 'lte' },
      { label: this.translateService.instant('FilterOperators.notEquals'), value: 'neq' }
    ];
  }

  ngAfterContentInit(): void {
    this.sorts = [...this.initialSort];
    this.groups = [...this.initialGroups];
    this.filters = [...this.initialFilter];
    this.bindFilterValue();
    this.rebuildTemplateMap();
    this.aggFields = Object.keys(this.groupAggregates || {});
    this.recompute();
  }

  ngOnInit() {
    this.filterChange$
      .pipe(debounceTime(300))
      .subscribe(f => this.filterChange.emit(f));

    // Setup server-side filter debouncing
    this.serverFilterSubject
      .pipe(
        debounceTime(this.serverSideFilterDebounce),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe(filters => {
        this.serverFilterChange.emit(filters);
      });

    // Client-side text/number filter debounce (400ms to let user type)
    this.filterInputSubject
      .pipe(debounceTime(400))
      .subscribe(({ field, type, value, operator }) => {
        this.setFilter(field, type, value, operator);
      });

    // Global search debounce
    this.globalSearch$
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe(() => this.recompute());
  }

  ngAfterViewInit(): void {
    // Defer auto-size until after the first render so getBoundingClientRect
    // returns real widths. setTimeout(0) is enough — we're not racing against
    // anything heavier than the browser layout pass.
    if (this.autoSizeColumns) {
      setTimeout(() => this.runAutoSize(), 0);
    }
  }

  // ── Column resize handlers ──────────────────────────────────────────────

  /**
   * True when this column should expose a resize handle. We check both the
   * grid-level toggle and the per-column override.
   */
  isColumnResizable(col: GridColumn<T>): boolean {
    if (!this.resizableColumns) return false;
    return col.resizable !== false;
  }

  /** Mouse-down on the resize handle starts a drag. */
  onResizeStart(event: MouseEvent, col: GridColumn<T>): void {
    if (!this.isColumnResizable(col)) return;
    event.preventDefault();
    event.stopPropagation();

    // Snapshot the current rendered width — even if `col.width` is unset,
    // the browser has laid out a real pixel width for the <th>. Use that as
    // the baseline so the drag feels natural.
    const th = (event.currentTarget as HTMLElement).closest('th') as HTMLElement | null;
    const rect = th?.getBoundingClientRect();
    this._resizeStartWidth = rect ? rect.width
      : (typeof col.width === 'number' ? col.width : 120);

    this._resizeCol = col;
    this._resizeStartX = event.clientX;

    // We listen on document so the drag keeps tracking even if the cursor
    // briefly leaves the header cell.
    document.addEventListener('mousemove', this._resizeMoveBound);
    document.addEventListener('mouseup', this._resizeEndBound);
  }

  private onResizeMove(event: MouseEvent): void {
    if (!this._resizeCol) return;
    const dx = event.clientX - this._resizeStartX;
    const min = this._resizeCol.minWidth ?? 60;
    const next = Math.max(min, this._resizeStartWidth + dx);
    this._resizeCol.width = Math.round(next);
    // OnPush — we mutated an @Input array element; nudge change-detection.
    this.cdr.markForCheck();
  }

  private onResizeEnd(): void {
    document.removeEventListener('mousemove', this._resizeMoveBound);
    document.removeEventListener('mouseup', this._resizeEndBound);
    if (this._resizeCol) {
      // Mark as user-resized so auto-size never overrides this width again.
      this.autoSizedFields.add(String(this._resizeCol.field));
    }
    this._resizeCol = null;
  }

  /**
   * Native browser tooltip for a data cell. Mirrors what the cell renders
   * (after `format()` if defined, else the raw field value) so users hovering
   * a truncated cell can read the full value. Returns null when there is no
   * meaningful text — null suppresses the title attribute entirely.
   */
  cellTooltip(row: T, col: GridColumn<T>): string | null {
    // Custom cell templates render their own content — leave them alone so the
    // template author can control tooltips per template.
    if (this.templateMap?.has(this.fieldKey(col.field))) return null;
    const raw = col.format ? col.format(row) : (row as any)?.[col.field as any];
    if (raw == null) return null;
    if (raw instanceof Date) return raw.toString();
    if (typeof raw === 'object') return null; // avoid `[object Object]`
    const s = String(raw);
    return s.length > 0 ? s : null;
  }

  // ── Auto-size based on content ──────────────────────────────────────────

  /**
   * Measure the visible rendered table and set each column's width to its
   * widest cell (with a small padding), capped at `maxAutoWidth`. Columns
   * that already have an explicit width or that the user has manually
   * resized are left alone.
   */
  private runAutoSize(): void {
    if (!this.autoSizeColumns) return;
    const host = this.hostRef.nativeElement;
    const table = host.querySelector<HTMLTableElement>('table');
    if (!table) return;
    const headerCells = table.querySelectorAll<HTMLTableCellElement>('thead th');
    if (!headerCells.length) return;

    // Build a column-index → field map by walking the visible columns. We
    // skip hidden / exportOnly columns since they don't participate in DOM.
    const visibleCols = this.columns.filter(c => !c.hidden && !c.exportOnly);

    // Sample at most 50 body rows for performance
    const bodyRows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr')).slice(0, 50);

    visibleCols.forEach((col, idx) => {
      // Skip columns that already carry an explicit width or that the user
      // touched manually via the drag handle.
      if (col.width != null || this.autoSizedFields.has(String(col.field))) return;

      const headerW = headerCells[idx]?.scrollWidth ?? 0;
      let maxBody = 0;
      for (const row of bodyRows) {
        const cell = row.children[idx] as HTMLElement | undefined;
        if (cell) maxBody = Math.max(maxBody, cell.scrollWidth);
      }
      const widest = Math.max(headerW, maxBody) + 16; // breathing room
      const final = Math.min(widest, this.maxAutoWidth);
      if (final > 0) {
        col.width = final;
        this.autoSizedFields.add(String(col.field));
      }
    });
    this.cdr.markForCheck();
  }

  bindFilterValue() {
    this.filters.forEach(item => {
      var column = this.columns.find(col => col.field.toString() == item.field.toString())
      if (column) {
        this.filterType[item.field.toString() + item.type] = item.operator;
        this.filterValue[item.field.toString() + item.type] = item.value;
      }
      var filterTypeSelectColumn = this.columns.find(col => col.field.toString() != item.field.toString() && col.filterType == "select" && (col.removeDefaultOption == undefined || col.removeDefaultOption == false));
      if (filterTypeSelectColumn)
        this.filterValue[filterTypeSelectColumn.field.toString() + filterTypeSelectColumn.filterType] = "";
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.resolveTitleKeys();
    this.aggFields = Object.keys(this.groupAggregates || {});
    if (this.resetSetting) {
      this.filterValue = {};
      this.clearFilters();
      this.clearSorts();
      this.clearGroups();
      this.columns.forEach(item => {
        var value = null;
        if (item.filterType == 'text')
          value = this.textFilter[0].value;
        if (item.filterType == 'number')
          value = this.numberFilter[0].value;
        if (item.filterType == 'date')
          value = this.dateFilter[0].value;
        this.filterType[item.field.toString() + item.filterType] = value;
      });
    }
    // Only reset sorts/filters when their @Input values explicitly change,
    // not when other inputs (e.g. data) change — preserves user's active filters
    if (changes['initialSort']) {
      this.sorts = [...this.initialSort];
    }
    if (changes['initialFilter']) {
      this.filters = [...this.initialFilter];
      this.bindFilterValue();
    }
    // Re-run auto-size when data lands. The first render in ngAfterViewInit
    // sees an empty <tbody> if `data` is loaded async; retrying here gives
    // runAutoSize() real cells to measure. runAutoSize is a no-op for columns
    // that already carry a width or are tracked in autoSizedFields, so this
    // does not override widths the user has dragged.
    if (changes['data'] && this.autoSizeColumns) {
      setTimeout(() => this.runAutoSize(), 0);
    }
    this.recompute();
  }

  private rebuildTemplateMap() {
    this.templateMap.clear();
    this.cellTemplates ?.forEach((d) => this.templateMap.set(d.field, d));
    this.editTemplateMap.clear();
    this.editTemplates ?.forEach((d) => this.editTemplateMap.set(d.field, d));
  }

  /** Resolve titleKey → title for columns that use i18n keys */
  private resolveTitleKeys(): void {
    if (!this.columns) return;
    for (const col of this.columns) {
      if (col.titleKey && !col.title) {
        col.title = this.translateService.instant(col.titleKey);
      }
    }
  }

  fieldKey(field: string | number | symbol): string {
    return String(field);
  }

  columnTitle(field: string): string {
    const c = this.columns.find((col) => this.fieldKey(col.field) === field);
    return c ?.title ?? field;
  }

  asDate(val: any): Date | null {
    if (val instanceof Date) return val;
    if (typeof val === 'string') {
      // handle yyyymmdd (e.g. 20250925)
      if (/^\d{8}$/.test(val)) {
        const y = +val.substring(0, 4);
        const m = +val.substring(4, 6) - 1;
        const dNum = +val.substring(6, 8);
        const d = new Date(y, m, dNum);
        return isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    }
    if (typeof val === 'number') {
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }

  isGroupedField(col: GridColumn): boolean {
    const f = this.fieldKey(col.field);
    return this.groups.some((g) => g.field === f);
  }

  valueFor(row: any, col: GridColumn): any {
    return this.getVal(row, this.fieldKey(col.field));
  }

  /**
   * Format value with number formatting (thousand separators) if enabled
   */
  formatValue(row: any, col: GridColumn): any {
    const value = col.format ? col.format(row) : this.valueFor(row, col);

    // Apply number formatting if enabled
    if (col.numberFormat && typeof value === 'number') {
      return this.formatNumber(value, col.decimals ?? 2);
    }

    return value;
  }

  /**
   * Format number with Norwegian thousand separators (1 000.00)
   * @param value Number to format
   * @param decimals Number of decimal places (0-4, default: 2)
   */
  /**
   * Excel number format for a column. Respects the column's `decimals` (0 → integer "#,##0",
   * N → "#,##0.{N zeros}"). Defaults to 2 decimals when not set, so existing grids are unchanged.
   */
  private exportNumFmt(col?: GridColumn<T>): string {
    const dec = col?.decimals;
    const places = (dec === undefined || dec === null) ? 2 : Math.max(0, Math.min(4, dec));
    return places > 0 ? `#,##0.${'0'.repeat(places)}` : '#,##0';
  }

  private formatNumber(value: number, decimals: number = 2): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '';
    }

    // Clamp decimals to valid range (0-4)
    const decimalPlaces = Math.max(0, Math.min(4, decimals));

    // Split into integer and decimal parts
    const parts = value.toFixed(decimalPlaces).split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    // Add thousand separators (space in Norwegian format)
    const withSeparators = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    // Return with decimal part only if decimals > 0
    if (decimalPlaces > 0 && decimalPart) {
      return `${withSeparators}.${decimalPart}`;
    }

    return withSeparators;
  }

  isSortedAsc(field: string | number | symbol): boolean {
    const f = this.fieldKey(field);
    for (let i = 0; i < this.sorts.length; i++) {
      const s = this.sorts[i];
      if (s.field === f && s.dir === 'asc') return true;
    }
    return false;
  }
  isSortedDesc(field: string | number | symbol): boolean {
    const f = this.fieldKey(field);
    for (let i = 0; i < this.sorts.length; i++) {
      const s = this.sorts[i];
      if (s.field === f && s.dir === 'desc') return true;
    }
    return false;
  }
  chevronClass(col: GridColumn): string {
    if (this.isSortedAsc(col.field)) return 'bi-chevron-up';
    if (this.isSortedDesc(col.field)) return 'bi-chevron-down';
    return '';
  }

  visibleColumns() {
    var columns = this.columns.filter((c) => !c.hidden && !c.exportOnly);
    columns.forEach(item => {
      if (this.filterType[item.field.toString() + item.filterType] == undefined) {
        var value = null;
        if (item.filterType == 'text')
          value = this.textFilter[0].value;
        if (item.filterType == 'number')
          value = this.numberFilter[0].value;
        if (item.filterType == 'date')
          value = this.dateFilter[0].value;
        this.filterType[item.field.toString() + item.filterType] = value;
      }
    });
    return columns;
  }

  hasAnyFilterableColumns(): boolean {
    return this.visibleColumns().some(col => col.filterable);
  }

  colWidth(c: GridColumn) {
    return c.width ? typeof c.width === 'number' ? `${c.width}px` : c.width : null;
  }
  isGrouped(): boolean {
    return this.groups.length > 0;
  }
  trackByRow = (_: number, row: T) =>
    this.rowKey ? this.rowKey(row) : (row as any);

  onHeaderClick(
    evt: MouseEvent,
    field: string | number | symbol,
    sortable?: boolean
  ) {
    if (!sortable) return;
    const multi = evt.shiftKey;
    this.toggleSort(this.fieldKey(field), multi);
  }

  toggleSort(field: string, multi = false) {
    const sorts = [...this.sorts];
    let ix = -1;
    for (let i = 0; i < sorts.length; i++)
      if (sorts[i].field === field) {
        ix = i;
        break;
      }
    if (ix >= 0) {
      const nextDir = sorts[ix].dir === 'asc' ? 'desc' : null;
      if (nextDir) sorts[ix].dir = nextDir as 'desc';
      else sorts.splice(ix, 1);
    } else {
      if (!multi) sorts.length = 0;
      sorts.push({ field, dir: 'asc' });
    }
    this.sorts = sorts;
    this.sortChange.emit(this.sorts);
    this.recompute();
  }

  /** Debounced filter input for text/number — gives user time to type */
  setFilterDebounced(
    field: string | number | symbol,
    type: GridColumn['filterType'],
    value: any,
    operator?: GridFilter['operator']
  ) {
    this.filterInputSubject.next({ field, type, value, operator });
  }

  setFilter(
    field: string | number | symbol,
    type: GridColumn['filterType'],
    value: any,
    operator?: GridFilter['operator']
  ) {
    const arr = [...this.filters];
    const fKey = this.fieldKey(field);
    let idx = -1;
    for (let i = 0; i < arr.length; i++)
      if (arr[i].field === fKey) {
        idx = i;
        break;
      }
    const op = operator || (type === 'text' ? 'contains' : 'eq');

    if (value === null || value === undefined || value === '') {
      if (idx >= 0) arr.splice(idx, 1);
    } else {
      const f: GridFilter = { field: fKey, type: type!, operator: op, value };
      if (idx >= 0) arr[idx] = f;
      else arr.push(f);
    }
    this.filters = arr;

    // Handle server-side search
    if (this.serverSideSearch) {
      this.updateServerSideFilters();
    } else {
      // Client-side filtering (existing behavior)
      this.recompute();
      this.filterChange$.next(this.filters);
    }
  }

  /**
   * Updates filter operator without forcing recompute/server call when no value is set yet.
   * This prevents accidental reloads while users are only changing filter method.
   */
  onFilterOperatorChange(
    field: string | number | symbol,
    type: GridColumn['filterType'],
    operator?: GridFilter['operator']
  ): void {
    const key = this.fieldKey(field) + String(type);
    const value = this.filterValue[key];

    // If no input value yet, just keep operator in UI state and wait for actual input.
    if (value === null || value === undefined || value === '') {
      return;
    }

    this.setFilter(field, type, value, operator);
  }

  /**
   * Convert grid filters to column filters and emit for server-side handling
   */
  private updateServerSideFilters(): void {
    const columnFilters: ColumnFilter[] = this.filters.map(f => ({
      field: f.field,
      operator: f.operator || 'contains',
      value: f.value
    }));

    this.activeColumnFilters = columnFilters;
    this.serverFilterSubject.next(columnFilters);
  }

  addGroup(field: string | number | symbol) {
    const fKey = this.fieldKey(field);
    if (!fKey) return;
    for (let i = 0; i < this.groups.length; i++)
      if (this.groups[i].field === fKey) return;
    this.groups = [...this.groups, { field: fKey, collapsed: false }];
    this.groupChange.emit(this.groups);
    this.recompute();
  }
  removeGroup(field: string) {
    this.groups = this.groups.filter((g) => g.field !== field);
    this.groupChange.emit(this.groups);
    this.recompute();
  }
  toggleGroupNode(node: GroupNode<any>) {
    if (node.kind === 'group') {
      // Toggle directly on the node — since we now slice processedTree directly
      // (not clone/filter), the paginatedTree nodes ARE the processedTree nodes.
      node.collapsed = !node.collapsed;
      this.cdr.detectChanges();
    }
  }

  /** Expand all groups. If level is provided, affects only that depth (0 = top groups). */
  expandAll(level?: number) {
    if (!this.processedTree.length) return;

    // Warn before expanding everything on very large datasets
    if (level === undefined && this.autoCollapseThreshold > 0
        && this.data.length > this.autoCollapseThreshold) {
      const confirmExpand = confirm(
        `Dette vil vise alle ${this.data.length.toLocaleString('nb-NO')} rader samtidig og kan gjøre nettleseren treg. Vil du fortsette?`
      );
      if (!confirmExpand) return;
    }

    const visit = (nodes: GroupNode<T>[], depth: number) => {
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        if (n.kind === 'group') {
          if (level === undefined || depth === level) (n as any).collapsed = false;
          // Only descend further if we want deeper levels or no specific level specified
          if (n.items && (level === undefined || depth < level)) {
            visit((n as any).items, depth + 1);
          }
        }
      }
    };
    visit(this.processedTree, 0);
    this.cdr.detectChanges();
  }

  /** Collapse all groups. If level is provided, affects only that depth. */
  collapseAll(level?: number) {
    if (!this.processedTree.length) return;
    const visit = (nodes: GroupNode<T>[], depth: number) => {
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        if (n.kind === 'group') {
          if (level === undefined || depth === level) (n as any).collapsed = true;
          if (n.items && (level === undefined || depth < level)) {
            visit((n as any).items, depth + 1);
          }
        }
      }
    };
    visit(this.processedTree, 0);
    this.cdr.detectChanges();
  }

  clearFilters() {
    this.filters = [];
    this.recompute();
  }
  clearSorts() {
    this.sorts = [];
    this.recompute();
  }
  clearGroups() {
    this.groups = [];
    this.groupChange.emit(this.groups);
    this.recompute();
  }

  /** Check if column has an active filter */
  hasActiveFilter(col: GridColumn<T>): boolean {
    const fKey = this.fieldKey(col.field);
    return this.filters.some(f => f.field === fKey);
  }

  /** Get active filters for chip display in toolbar */
  getActiveFiltersForChips(): { field: string; label: string }[] {
    return this.filters.map(f => {
      const col = this.columns.find(c => this.fieldKey(c.field) === f.field);
      const colTitle = col?.title || f.field;
      const opLabel = this.getOperatorDisplayLabel(f.operator, f.type);
      let valLabel = String(f.value);
      if (f.type === 'select' && col?.selectOptions) {
        const opt = col.selectOptions.find(o => String(o.value) === String(f.value));
        if (opt) valLabel = opt.label;
      }
      return { field: f.field, label: `${colTitle} ${opLabel} ${valLabel}` };
    });
  }

  /** Remove filter for a specific field */
  removeFilterByField(field: string): void {
    const col = this.columns.find(c => this.fieldKey(c.field) === field);
    if (col?.filterType) {
      const key = field + col.filterType;
      this.filterValue[key] = col.filterType === 'select' ? '' : null;
    }
    this.filters = this.filters.filter(f => f.field !== field);
    if (this.serverSideSearch) {
      this.updateServerSideFilters();
    } else {
      this.recompute();
      this.filterChange$.next(this.filters);
    }
  }

  /** Focus the filter input inside the popover when it opens */
  focusFilterInput(popover: any): void {
    setTimeout(() => {
      const win = popover._windowRef;
      if (win) {
        const el: HTMLElement = win.location.nativeElement;
        const input = el.querySelector<HTMLElement>('input, select.form-select-sm');
        input?.focus();
      }
    }, 0);
  }

  /** Toggle the inline filter row below the header */
  toggleFilterRow(): void {
    this.filterRowVisible = !this.filterRowVisible;
    this.cdr.detectChanges();
  }

  /** Get display label for filter operator */
  private getOperatorDisplayLabel(op: string | undefined, type: string): string {
    switch (op) {
      case 'contains': return '≈';
      case 'startsWith': return 'begynner med';
      case 'endsWith': return 'slutter med';
      case 'eq': return '=';
      case 'neq': return '≠';
      case 'gt': return '>';
      case 'gte': return '≥';
      case 'lt': return '<';
      case 'lte': return '≤';
      default: return type === 'text' ? '≈' : '=';
    }
  }

  // ---------- processing ----------
  private recompute() {
    if (!this.data) return; // Guard against undefined data during initialization

    // Show loader only for very large datasets where filter/sort/group takes noticeable time.
    // For <5000 rows the JS computation is <5ms — the 50ms setTimeout delay and extra
    // detectChanges() would hurt perceived performance more than they help.
    if (this.data.length > 5000) {
      this.isProcessing = true;
      this.cdr.detectChanges(); // Force UI update to show spinner

      // Use setTimeout to allow spinner to render before heavy processing
      setTimeout(() => {
        this.performRecompute();
      }, 50);
    } else {
      // For normal datasets, process immediately (no artificial delay)
      this.performRecompute();
    }
  }

  private performRecompute() {
    this.nodeAgg = new WeakMap<object, AggMap>(); // reset cache

    const filtered = this.applyFilters(this.data);
    const sorted = this.applySorts(filtered);

    // Calculate total aggregates for the entire dataset
    if (this.showTotalRow && this.aggFields.length > 0) {
      this.totalAggregates = this.calcAgg(sorted);
    } else {
      this.totalAggregates = {};
    }

    if (this.groups.length) {
      this.processedTree = this.groupData(sorted, this.groups);
      // Keep sorted array for pagination purposes (need to know all rows for page calculation)
      this.flatRows = sorted;
    } else {
      this.processedTree = [];
      this.flatRows = sorted;
    }

    if (this.showPagination) {
      if (this.serverSidePagination == false) {
        if (this.groups.length) {
          // For grouped data: paginate by TOP-LEVEL GROUPS (not individual rows)
          // This ensures a group is never split across pages
          this.totalRows = this.processedTree.length;
          this.loadGroupedDataByAllRows();
        } else {
          // For flat data: use existing pagination logic
          this.rows = this.flatRows;
          this.totalRows = this.flatRows.length;
          this.loadData();
        }
      }
      else {
        this.rows = this.flatRows;
        this.totalRows = this.totalRows;
      }
    }
    else {
      if (this.groups.length) {
        // No pagination: use full tree
        this.paginatedTree = this.processedTree;
      } else {
        this.rows = this.flatRows;
      }
      this.totalRows = this.groups.length ? this.getVisibleRows().length : this.flatRows.length;
    }

    // Hide loader
    this.isProcessing = false;
    this.cdr.detectChanges();
  }

  /** Return the rows that are currently visible (after filters, sorts, and expanded groups). */
  private getVisibleRows(): T[] {
    if (!this.isGrouped()) {
      return [...this.flatRows];
    }
    const out: T[] = [];
    const visit = (nodes: GroupNode<T>[]) => {
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        if ((n as any).kind === 'group') {
          const g = n as any as GroupNode<T> & { items: GroupNode<T>[]; collapsed?: boolean };
          if (!g.collapsed) visit(g.items);
        } else if ((n as any).kind === 'row') {
          const r = n as any as { row: T };
          out.push(r.row);
        }
      }
    };
    visit(this.processedTree);
    return out;
  }

  private getVal(row: any, field: string) {
    // Safety check: return undefined if field is null/undefined
    if (!field || typeof field !== 'string') {
      console.warn('getVal called with invalid field:', field);
      return undefined;
    }

    const parts = field.split('.');
    let acc: any = row;
    for (let i = 0; i < parts.length; i++) {
      acc = acc ?.[parts[i]];
      if (acc === undefined || acc === null) break;
    }
    return acc;
  }

  /** Handle global search input */
  onGlobalSearch(term: string): void {
    this.globalSearchTerm = term;
    this.globalSearch$.next(term);
  }

  private applyGlobalSearch(rows: T[]): T[] {
    const term = (this.globalSearchTerm || '').trim().toLowerCase();
    if (!term) return rows;
    const cols = this.columns.filter(c => !c.hidden && !c.exportOnly);
    return rows.filter((r: any) => {
      for (let i = 0; i < cols.length; i++) {
        const val = this.getVal(r, this.fieldKey(cols[i].field));
        if (val != null && String(val).toLowerCase().includes(term)) return true;
      }
      return false;
    });
  }

  private applyFilters(rows: T[]): T[] {
    // Apply global search first
    rows = this.applyGlobalSearch(rows);
    if (!this.filters.length) return rows;
    return rows.filter((r: any) => {
      for (let i = 0; i < this.filters.length; i++) {
        const f = this.filters[i];
        const v = this.getVal(r, f.field);
        if (v === undefined || (v === null && f.operator != "neq")) return false;
        if (f.type === 'text') {
          const txt = String(v).toLowerCase();
          const val = String(f.value).toLowerCase();
          if (f.operator === 'startsWith' && !txt.startsWith(val)) return false;
          if (f.operator === 'endsWith' && !txt.endsWith(val)) return false;
          if (f.operator === 'eq' && txt !== val) return false;
          if (f.operator === 'neq' && txt === val) return false;
          if (!f.operator || f.operator === 'contains') {
            if (!txt.includes(val)) return false;
          }
        } else if (f.type === 'number') {
          const n = Number(v),
            comp = Number(f.value);
          if (isNaN(n) || isNaN(comp)) return false;
          if (f.operator === 'eq' && n !== comp) return false;
          if (f.operator === 'neq' && n === comp) return false;
          if (f.operator === 'lt' && n >= comp) return false;
          if (f.operator === 'lte' && n > comp) return false;
          if (f.operator === 'gt' && n <= comp) return false;
          if (f.operator === 'gte' && n < comp) return false;
          if (!f.operator && n !== comp) return false;
        } else if (f.type === 'date') {
          const d = this.toDate(moment(v, 'YYYYMMDD')).setHours(0, 0, 0, 0);
          const comp = this.toDate(f.value).setHours(0, 0, 0, 0);
          if (f.operator === 'eq' && d !== comp) return false;
          if (f.operator === 'lt' && d >= comp) return false;
          if (f.operator === 'lte' && d > comp) return false;
          if (f.operator === 'gt' && d <= comp) return false;
          if (f.operator === 'gte' && d < comp) return false;
          if (f.operator === 'neq' && d === comp) return false;
          if (!f.operator && d !== comp) return false;
        } else if (f.type === 'select') {
          // Handle type conversion for select filters
          // HTML select elements return strings, but data might be boolean, number, etc.
          let filterValue = f.value;
          let dataValue = v;

          // Convert string filter values to appropriate types for comparison
          if (typeof v === 'boolean') {
            filterValue = filterValue === 'true';
          } else if (typeof v === 'number') {
            filterValue = Number(filterValue);
          }

          if (dataValue !== filterValue) return false;
        }
      }
      return true;
    });
  }

  private toDate(v: unknown): Date {
    return v instanceof Date ? v : new Date(v as any);
  }

  private applySorts(rows: T[]): T[] {
    if (!this.sorts.length) return [...rows];
    // Filter out invalid sort configurations
    const sorts = this.sorts.filter(s => s && s.field && typeof s.field === 'string');
    if (!sorts.length) return [...rows];

    return [...rows].sort((a: any, b: any) => {
      for (let i = 0; i < sorts.length; i++) {
        const s = sorts[i];
        const av = this.getVal(a, s.field);
        const bv = this.getVal(b, s.field);
        if (av == null && bv == null) continue;
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = av > bv ? 1 : av < bv ? -1 : 0;
        if (cmp !== 0) return s.dir === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }

  private groupData(rows: T[], groups: GridGroup[], level = 0): GroupNode<T>[] {
    if (!groups.length)
      return rows.map((r) => ({ kind: 'row', row: r } as GroupNode<T>));
    const field = groups[0].field;

    // Auto-collapse when total row count exceeds threshold
    const autoCollapse = this.autoCollapseThreshold > 0
      && this.data.length > this.autoCollapseThreshold
      && level === 0;

    const map = new Map<any, T[]>();
    for (let i = 0; i < rows.length; i++) {
      const r: any = rows[i];
      const key = this.getVal(r, field);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }

    const nodes: GroupNode<T>[] = [];
    map.forEach((items, val) => {
      const child = this.groupData(items, groups.slice(1), level + 1);
      const node: GroupNode<T> = {
        kind: 'group',
        field,
        value: val,
        items: child,
        count: items.length,
        collapsed: autoCollapse,
      } as any;

      // Beregn aggregater på alle rader i denne gruppen
      const agg = this.calcAgg(items);
      this.nodeAgg.set(node, agg);

      nodes.push(node);
    });

    // sort grupper etter gruppens felt (respektér sort på feltet)
    let dir: 'asc' | 'desc' = 'asc';
    for (let i = 0; i < this.sorts.length; i++)
      if (this.sorts[i].field === field) {
        dir = this.sorts[i].dir;
        break;
      }
    nodes.sort((a: any, b: any) => {
      const av: any = a.value,
        bv: any = b.value;
      const cmp = av > bv ? 1 : av < bv ? -1 : 0;
      return dir === 'desc' ? -cmp : cmp;
    });

    return nodes;
  }

  private calcAgg(items: T[]): AggMap {
    const cfg = this.groupAggregates || {};
    const filters = this.aggregateFilters || {};
    const fields = Object.keys(cfg);
    const out: AggMap = {};
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      const kinds = cfg[f];
      const fieldFilters = filters[f] || {};

      for (const kind of kinds) {
        const filter = fieldFilters[kind];
        const filteredItems = filter ? items.filter(filter) : items;

        const nums: number[] = [];
        for (let j = 0; j < filteredItems.length; j++) {
          const v = this.getVal(filteredItems[j] as any, f);
          const n = Number(v);
          if (!Number.isNaN(n)) nums.push(n);
        }

        if (!out[f]) out[f] = {};
        const m = out[f];

        if (kind === 'sum') m.sum = nums.reduce((a, b) => a + b, 0);
        if (kind === 'count') m.count = nums.length;
        if (kind === 'avg')
          m.avg = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
        if (kind === 'min')
          m.min = nums.length ? Math.min(...nums) : undefined;
        if (kind === 'max')
          m.max = nums.length ? Math.max(...nums) : undefined;
      }
    }
    return out;
  }

  /** Les verdi for node-aggregat i template */
  aggVal(node: GroupNode<T>, field: string, kind: AggKind): number | undefined {
    const m = this.nodeAgg.get(node);
    return m ?.[field] ?.[kind];
  }

  /** Les verdi for total-aggregat i template */
  totalAggVal(field: string, kind: AggKind): number | undefined {
    return this.totalAggregates ?.[field] ?.[kind];
  }

  changeData(event) {
    const checked = event.target.checked as boolean;
    const rows = this.getVisibleRows();
    this.allChecked.emit({ checked, rows });
  }

  /** Handle aggregation filter toggle */
  onAggregateFilterToggle(enabled: boolean): void {
    if (this.aggregateFilterConfig ?.onToggle) {
      this.aggregateFilterConfig.onToggle(enabled);
    }
  }

  /** Export current data to Excel */
  async exportToExcel(): Promise<void> {
    const dataToExport = this.isGrouped() ? this.getVisibleRows() : this.flatRows;

    if (!dataToExport || dataToExport.length === 0) {
      console.warn('No data to export');
      return;
    }

    // Build report columns from ALL columns (including exportOnly) excluding __actions__ and other special columns
    const reportColumns: ReportColumn<T>[] = this.columns
      .filter(col => !col.hidden && !this.fieldKey(col.field).startsWith('__'))
      .map(col => ({
        field: this.fieldKey(col.field),
        label: col.title || this.fieldKey(col.field),
        align: 'left',
        format: col.format ? (row: T) => {
          const formatted = col.format!(row);
          return formatted != null ? String(formatted) : '';
        } : undefined,
      }));

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = this.exportFilename || `export_${timestamp}`;

    try {
      await this.reportExportService.exportExcel(filename, reportColumns, dataToExport);
    } catch (error) {
      console.error('Export error:', error);
    }
  }

  /** Export current data to Excel with formatting */
  async exportToExcelFormatted(): Promise<void> {
    const buffer = await this.exportToBuffer();
    if (!buffer) return;

    // Lazy-load file-saver only when actually downloading.
    const fileSaverMod: any = await import('file-saver');
    const saveAs: (data: Blob, filename: string) => void =
      fileSaverMod.saveAs || fileSaverMod.default?.saveAs || fileSaverMod.default;

    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${this.exportFilename || `export_${timestamp}`}_formatert.xlsx`;
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      filename
    );
  }

  /**
   * Builds the formatted Excel workbook (same layout as the Export button) and returns the
   * raw .xlsx bytes WITHOUT downloading. Returns null when there is no data to export. Used
   * e.g. to attach the report as a voucher document on the server.
   */
  async exportToBuffer(useAllRows = false): Promise<ArrayBuffer | null> {
    // useAllRows ignores the grid's active search/column filters and exports the full dataset
    // (sorted) — used when the export must document the complete data, not the filtered view.
    const grouped = !useAllRows && this.isGrouped() && this.groups.length > 0;
    const dataToExport = useAllRows
      ? this.applySorts(this.data)
      : (this.isGrouped() ? this.getVisibleRows() : this.flatRows);

    if (!dataToExport || dataToExport.length === 0) {
      console.warn('No data to export');
      return null;
    }

    // Lazy-load ExcelJS only when actually exporting — keeps ~700 kB out of the
    // eagerly-loaded bundle. The `.default || mod` fallback protects against bundlers
    // that wrap CommonJS modules under `.default` in dynamic-import shape.
    const excelMod: any = await import('exceljs');
    const ExcelJS: typeof ExcelJSType = excelMod.default || excelMod;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Data');

    // Title row
    const title = 'Rapport';
    const titleRow = worksheet.addRow([title]);
    titleRow.font = { bold: true, size: 16 };
    titleRow.height = 24;

    // Merge cells across all export columns (including exportOnly)
    const visibleCols = this.columns.filter(col => !col.hidden && !this.fieldKey(col.field).startsWith('__'));
    if (visibleCols.length > 1) {
      worksheet.mergeCells(1, 1, 1, visibleCols.length);
    }

    // Metadata row
    worksheet.addRow([`Eksportert: ${new Date().toLocaleString('nb-NO')}`]).font = { size: 11 };
    worksheet.addRow([]); // Empty row

    // Header row
    const headerRow = worksheet.addRow(visibleCols.map(col => col.title || this.fieldKey(col.field)));
    headerRow.font = { bold: true, size: 11 };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
    headerRow.alignment = { horizontal: 'left', vertical: 'middle' };
    headerRow.height = 20;

    // Check if data is grouped (full-data export is always flat)
    if (grouped) {
      // Export with grouping structure
      this.writeGroupedRows(worksheet, this.processedTree, visibleCols, 0);
    } else {
      // Export flat data
      dataToExport.forEach((row: T) => {
        const rowData = visibleCols.map(col => {
          const value = this.getExportValue(row, col);
          // Keep numbers as numbers for Excel formatting
          if (typeof value === 'number') {
            return value;
          }
          return value != null ? String(value) : '';
        });
        const dataRow = worksheet.addRow(rowData);
        dataRow.font = { size: 10 };
        dataRow.alignment = { horizontal: 'left', vertical: 'middle' };

        // Format numeric cells with Norwegian number format
        dataRow.eachCell((cell, colNumber) => {
          if (typeof cell.value === 'number') {
            cell.numFmt = this.exportNumFmt(visibleCols[colNumber - 1]);
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          }
        });
      });
    }

    // Auto-fit columns
    worksheet.columns.forEach((column, index) => {
      if (column) {
        const col = visibleCols[index];
        column.width = col ?.width ? Number(col.width) / 7 : 15;
      }
    });

    // Freeze header rows
    worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 4 }];

    const buffer = await workbook.xlsx.writeBuffer();
    return buffer as ArrayBuffer;
  }

  /** Helper to write grouped rows with proper formatting */
  private writeGroupedRows(worksheet: ExcelJSType.Worksheet, nodes: GroupNode<T>[], visibleCols: GridColumn<T>[], level: number): void {
    nodes.forEach(node => {
      if (node.kind === 'group') {
        const groupNode = node as any;

        // Group header row
        const groupLabel = `${this.getColumnTitle(groupNode.field)}: ${groupNode.value || '(tom)'}`;
        const groupHeaderRow = worksheet.addRow([groupLabel]);
        groupHeaderRow.font = { bold: true, size: 12, color: { argb: 'FF000000' } };
        groupHeaderRow.height = 18;
        groupHeaderRow.outlineLevel = level;

        // Merge across all columns
        if (visibleCols.length > 1) {
          const lastCol = visibleCols.length;
          worksheet.mergeCells(groupHeaderRow.number, 1, groupHeaderRow.number, lastCol);
        }

        // Process child items (recursively for nested groups or rows)
        if (groupNode.items && groupNode.items.length > 0) {
          this.writeGroupedRows(worksheet, groupNode.items, visibleCols, level + 1);
        }

        // Group summary row if aggregates are configured
        if (this.showTotalRow && this.groupAggregates) {
          const summaryData: any[] = visibleCols.map((col, idx) => {
            const field = this.fieldKey(col.field);

            // Check if this field has aggregates
            const fieldAggs = this.groupAggregates[field];
            if (fieldAggs && fieldAggs.length > 0) {
              const agg = this.nodeAgg.get(node);
              if (agg && agg[field]) {
                const aggKind = fieldAggs[0]; // Use first aggregate kind
                const value = agg[field][aggKind];
                if (value !== undefined) {
                  return idx === 0 ? `Sum:` : value;
                }
              }
            }

            return idx === 0 ? `Sum:` : '';
          });

          const summaryRow = worksheet.addRow(summaryData);
          summaryRow.font = { bold: true, size: 11 };
          summaryRow.alignment = { horizontal: 'left', vertical: 'middle' };
          summaryRow.height = 16;
          summaryRow.outlineLevel = level;
          summaryRow.eachCell((cell, colNumber) => {
            cell.border = {
              top: { style: 'thin', color: { argb: 'FF000000' } }
            };
            // Format numeric cells with Norwegian number format
            if (typeof cell.value === 'number') {
              cell.numFmt = this.exportNumFmt(visibleCols[colNumber - 1]);
              cell.alignment = { horizontal: 'right', vertical: 'middle' };
            }
          });
        }

        // Empty row after group
        const emptyRow = worksheet.addRow([]);
        emptyRow.outlineLevel = level;

      } else {
        // Regular data row
        const row = (node as any).row as T;
        const rowData = visibleCols.map(col => {
          const value = this.getExportValue(row, col);
          // Keep numbers as numbers for Excel formatting
          if (typeof value === 'number') {
            return value;
          }
          return value != null ? String(value) : '';
        });

        const dataRow = worksheet.addRow(rowData);
        dataRow.font = { size: 10 };
        dataRow.alignment = { horizontal: 'left', vertical: 'middle' };
        dataRow.outlineLevel = level;

        // Format numeric cells with Norwegian number format
        dataRow.eachCell((cell, colNumber) => {
          if (typeof cell.value === 'number') {
            cell.numFmt = this.exportNumFmt(visibleCols[colNumber - 1]);
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          }
        });
      }
    });
  }

  /** Get column title by field name */
  private getColumnTitle(field: string): string {
    const col = this.columns.find(c => this.fieldKey(c.field) === field);
    return col ?.title || field;
  }

  /**
   * Get export value for a column (handles exportOnly columns correctly)
   * Priority: 1) format function, 2) raw value from row[field]
   * Note: Templates are ignored for exportOnly columns (they don't render in grid)
   */
  private getExportValue(row: T, col: GridColumn<T>): any {
    const fieldKey = this.fieldKey(col.field);
    const rawValue = this.getValue(row, fieldKey);
    
    // 1. If format function exists, use it
    if (col.format) {
      return col.format(row);
    }
    
    // 2. Return raw value
    return rawValue;
  }

  /** Alias for backward compatibility */
  async exportToExcelRaw(): Promise<void> {
    return this.exportToExcel();
  }

  private getValue(row: T, field: string): any {
    const keys = field.split('.');
    let value: any = row;
    for (const key of keys) {
      if (value == null) return null;
      value = value[key];
    }
    return value;
  }

  // loadData(changed = true) {
  //   var skip = (this.currentPage - 1) * this.pageSize;
  //   this.rows = this.flatRows.slice(skip, skip + this.pageSize);
  //   if (changed)
  //     this.pageChange.emit({ currentPage: this.currentPage, pageSize: +this.pageSize });
  // }

  loadData() {
    if (!this.serverSidePagination) {
      if (this.groups.length > 0) {
        // Grouped mode: paginate by top-level groups
        this.loadGroupedDataByAllRows();
      } else {
        // Flat mode: slice rows for the current page
        var skip = (this.currentPage - 1) * this.pageSize;
        this.rows = this.flatRows.slice(skip, skip + this.pageSize);
      }
    }
    if (this.getAllCheckBox) {
      this.checkedAllCheckBox = false;
      this.currentPageChange.emit(this.currentPage);
    }
  }

  changePageSize() {
    this.pageChange.emit({ currentPage: this.currentPage, pageSize: +this.pageSize });
    this.loadData();
  }

  /**
   * Load paginated grouped data by slicing top-level groups.
   * This ensures that all rows belonging to a group stay together on the same page.
   * Pagination is based on the number of top-level groups, not individual rows.
   */
  loadGroupedDataByAllRows() {
    const skip = (this.currentPage - 1) * this.pageSize;
    this.paginatedTree = this.processedTree.slice(skip, skip + this.pageSize);
    this.cdr.detectChanges();
  }

  /**
   * Start editing a cell
   */
  startEdit(row: T, col: GridColumn<T>): void {
    if (!col.editable) return;

    const rowKeyValue = this.rowKey ? this.rowKey(row) : this.data.indexOf(row);
    this.editingCell = { rowKey: rowKeyValue, field: this.fieldKey(col.field) };
    this.editingValue = this.valueFor(row, col);
    this.editError = null;
    this.cdr.markForCheck();
  }

  /**
   * Save edited value
   */
  saveEdit(row: T, col: GridColumn<T>): void {
    if (!this.editingCell) return;

    const oldValue = this.valueFor(row, col);
    let newValue = this.editingValue;

    // Type conversion for numbers
    if (col.editType === 'number' && typeof newValue === 'string') {
      newValue = parseFloat(newValue);
      if (isNaN(newValue)) {
        this.editError = 'Invalid number';
        return;
      }
    }

    // Validate
    if (col.editValidator) {
      const validationResult = col.editValidator(newValue, row);
      if (validationResult !== true) {
        this.editError = typeof validationResult === 'string' ? validationResult : 'Invalid value';
        return;
      }
    }

    // Emit event
    this.cellEdit.emit({
      row,
      field: col.field,
      oldValue,
      newValue
    });

    // Clear editing state
    this.cancelEdit();
  }

  /**
   * Cancel editing
   */
  cancelEdit(): void {
    this.editingCell = null;
    this.editingValue = null;
    this.editError = null;
    this.cdr.markForCheck();
  }

  /**
   * Returns a function that saves the edit for use in custom edit template context.
   * Usage in template: [ngTemplateOutletContext]="{ save: saveEditFn(row, col) }"
   * In custom template: <button (click)="save(newValue)">
   */
  saveEditFn(row: T, col: GridColumn<T>): (value?: any) => void {
    return (value?: any) => {
      if (value !== undefined) {
        this.editingValue = value;
      }
      this.saveEdit(row, col);
    };
  }

  /**
   * Returns a function that cancels editing for use in custom edit template context.
   */
  cancelEditFn(): () => void {
    return () => this.cancelEdit();
  }

  /**
   * Check if a cell is being edited
   */
  isCellEditing(row: T, col: GridColumn<T>): boolean {
    if (!this.editingCell) return false;
    const rowKeyValue = this.rowKey ? this.rowKey(row) : this.data.indexOf(row);
    return this.editingCell.rowKey === rowKeyValue &&
      this.editingCell.field === this.fieldKey(col.field);
  }

  // ───── Mobile card helpers ─────

  /** Get a stable key for a row (used for expand/collapse tracking) */
  mobileRowKey(row: T): string | number {
    return this.rowKey ? this.rowKey(row) : this.data.indexOf(row);
  }

  /** Toggle expand/collapse for a mobile card */
  toggleMobileExpand(row: T): void {
    const key = this.mobileRowKey(row);
    if (this.mobileExpandedRows.has(key)) {
      this.mobileExpandedRows.delete(key);
    } else {
      this.mobileExpandedRows.add(key);
    }
    this.cdr.markForCheck();
  }

  /** Check if a mobile card is expanded */
  isMobileExpanded(row: T): boolean {
    return this.mobileExpandedRows.has(this.mobileRowKey(row));
  }

  /**
   * Auto-detect mobile role for a column based on field name, type, and position.
   * Explicit mobileRole on the column always wins.
   */
  private detectMobileRole(col: GridColumn<T>, index: number, cols: GridColumn<T>[]): GridColumn<T>['mobileRole'] {
    if (col.mobileRole) return col.mobileRole;

    const field = this.fieldKey(col.field).toLowerCase();

    // Actions column (typically named __actions__ or has no field with template)
    if (field === '__actions__' || field === 'actions' || field === '__actions') return 'actions';

    // Status/priority/type fields → badge
    if (/status|priority|prio|type|state|active|isactive|category/.test(field)) return 'badge';

    // Date fields → meta
    if (/date|dato|due|deadline|created|updated|start|end|time|timestamp/.test(field)) return 'meta';

    // First visible string-like column that isn't a badge/meta → title
    const visibleCols = cols.filter(c => !c.hidden && !c.exportOnly);
    const firstStringCol = visibleCols.find(c => {
      const f = this.fieldKey(c.field).toLowerCase();
      return !/status|priority|prio|type|state|active|isactive|category/.test(f)
        && !/date|dato|due|deadline|created|updated|start|end|time|timestamp/.test(f)
        && f !== '__actions__' && f !== 'actions' && f !== '__actions';
    });
    if (firstStringCol && this.fieldKey(firstStringCol.field) === this.fieldKey(col.field)) return 'title';

    // Second visible non-badge/meta/actions → subtitle
    const titleFound = visibleCols.findIndex(c => this.fieldKey(c.field) === this.fieldKey(firstStringCol?.field));
    const subtitleCol = visibleCols.find((c, i) => {
      if (i <= titleFound) return false;
      const f = this.fieldKey(c.field).toLowerCase();
      return !/status|priority|prio|type|state|active|isactive|category/.test(f)
        && !/date|dato|due|deadline|created|updated|start|end|time|timestamp/.test(f)
        && f !== '__actions__' && f !== 'actions' && f !== '__actions';
    });
    if (subtitleCol && this.fieldKey(subtitleCol.field) === this.fieldKey(col.field)) return 'subtitle';

    return 'meta'; // default fallback
  }

  /** Get the effective mobile priority for a column */
  private getMobilePriority(col: GridColumn<T>, role: GridColumn<T>['mobileRole']): number {
    if (col.mobilePriority !== undefined) return col.mobilePriority;
    switch (role) {
      case 'title': return 0;
      case 'badge': return 10;
      case 'subtitle': return 20;
      case 'meta': return 30;
      case 'actions': return 40;
      case 'hidden': return 999;
      default: return 50;
    }
  }

  /** Get columns for mobile header (priority < 50) */
  getMobileHeaderCols(): GridColumn<T>[] {
    const visible = this.visibleColumns();
    return visible
      .map((col, i) => ({ col, role: this.detectMobileRole(col, i, this.columns) }))
      .filter(x => x.role !== 'hidden' && x.role !== 'actions' && this.getMobilePriority(x.col, x.role) < 50)
      .sort((a, b) => this.getMobilePriority(a.col, a.role) - this.getMobilePriority(b.col, b.role))
      .map(x => x.col);
  }

  /** Get columns for mobile details section (priority >= 50, excludes actions) */
  getMobileDetailCols(): GridColumn<T>[] {
    const visible = this.visibleColumns();
    return visible
      .map((col, i) => ({ col, role: this.detectMobileRole(col, i, this.columns) }))
      .filter(x => x.role !== 'hidden' && x.role !== 'actions' && this.getMobilePriority(x.col, x.role) >= 50)
      .sort((a, b) => this.getMobilePriority(a.col, a.role) - this.getMobilePriority(b.col, b.role))
      .map(x => x.col);
  }

  /** Get the actions column (if any) */
  getMobileActionsCol(): GridColumn<T> | null {
    const visible = this.visibleColumns();
    return visible.find((col, i) => this.detectMobileRole(col, i, this.columns) === 'actions') || null;
  }

  /** Get the title column for mobile */
  getMobileTitleCol(): GridColumn<T> | null {
    const visible = this.visibleColumns();
    return visible.find((col, i) => this.detectMobileRole(col, i, this.columns) === 'title') || null;
  }

  /** Get badge columns for mobile */
  getMobileBadgeCols(): GridColumn<T>[] {
    const visible = this.visibleColumns();
    return visible.filter((col, i) => this.detectMobileRole(col, i, this.columns) === 'badge');
  }

  /** Check if there are detail columns to expand */
  hasMobileDetails(): boolean {
    return this.getMobileDetailCols().length > 0;
  }

  /** Get the effective mobile role for a column (for use in template) */
  getMobileRole(col: GridColumn<T>): GridColumn<T>['mobileRole'] {
    const visible = this.visibleColumns();
    const index = visible.indexOf(col);
    return this.detectMobileRole(col, index, this.columns);
  }
}